import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import {
  contactsQuery,
  listsQuery,
  useBulkList,
  useBulkDelete,
  downloadContactsCsv,
  type ContactFilters,
} from '../lib/contacts.js';
import { NewContactDialog } from '../components/NewContactDialog.js';
import { ListManagerDialog } from '../components/ListManagerDialog.js';
import { SuppressContactDialog } from '../components/SuppressContactDialog.js';

/** „1 Kontakt", nicht „1 Kontakte" — die Meldung wird oft genug gelesen. */
function kontakte(anzahl: number): string {
  return `${anzahl.toLocaleString('de-CH')} ${anzahl === 1 ? 'Kontakt' : 'Kontakte'}`;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  unsubscribed: 'Abgemeldet',
  bounced: 'Unzustellbar',
  complained: 'Beschwerde',
  suppressed: 'Gesperrt',
};

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [listId, setListId] = useState('');
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [listManagerOpen, setListManagerOpen] = useState(false);

  /**
   * Die Auswahl haelt Kennungen, nicht Zeilen.
   *
   * Ein Filter, der sich zwischen Auswaehlen und Ausfuehren aendert — durch
   * einen Import, einen Bounce, eine zweite Sitzung — traefe sonst andere
   * Kontakte als die, die jemand gesehen hat. Bei einer Loeschung ist das der
   * Unterschied zwischen zwoelf und zwoelftausend.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkListId, setBulkListId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const bulkList = useBulkList();
  const bulkDelete = useBulkDelete();

  const [suppressContact, setSuppressContact] = useState<{ id: string; email: string } | null>(null);

  const filters: ContactFilters = {
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(listId ? { listId } : {}),
    ...(cursorStack.length > 0 ? { cursor: cursorStack[cursorStack.length - 1] } : {}),
  };

  const contacts = useQuery({ ...contactsQuery(filters), placeholderData: keepPreviousData });
  const lists = useQuery(listsQuery);

  /**
   * Jede Filteraenderung setzt die Blaetterposition zurueck — und die Auswahl.
   * Eine Auswahl, die nach einem Filterwechsel unsichtbar weiterlebt, ist die
   * Vorstufe zur versehentlichen Massenloeschung.
   */
  function changeFilter(apply: () => void) {
    apply();
    setCursorStack([]);
    clearSelection();
  }

  function clearSelection() {
    setSelected(new Set());
    setConfirmDelete(false);
  }

  const page = contacts.data;
  const isEmpty = !contacts.isLoading && page?.items.length === 0;
  const hasFilters = Boolean(search || status || listId);

  const pageIds = page?.items.map((c) => c.id) ?? [];
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPageSelected = pageIds.some((id) => selected.has(id));

  function toggleOne(id: string, on: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
    setConfirmDelete(false);
  }

  function togglePage(on: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of pageIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
    setConfirmDelete(false);
  }

  const ids = [...selected];
  const listName = lists.data?.items.find((l) => l.id === bulkListId)?.name ?? 'Liste';

  async function runListAction(action: 'add' | 'remove' | 'move') {
    if (!bulkListId || ids.length === 0) return;
    const result = await bulkList.mutateAsync({ ids, listId: bulkListId, action });
    const verb = action === 'add' ? 'hinzugefügt' : action === 'remove' ? 'entfernt' : 'verschoben';
    setBulkNotice(
      `${kontakte(result.affected)} ${verb} — „${listName}".` +
        (result.skipped > 0 ? ` ${result.skipped} übersprungen.` : ''),
    );
    clearSelection();
  }

  /** Exportiert genau das, was die aktuellen Filter zeigen — nicht nur die Seite. */
  async function exportieren() {
    setExporting(true);
    setBulkNotice(null);
    try {
      await downloadContactsCsv(filters);
      setBulkNotice('Der Export wurde heruntergeladen.');
    } catch (err) {
      setBulkNotice(err instanceof Error ? err.message : 'Der Export ist fehlgeschlagen.');
    } finally {
      setExporting(false);
    }
  }

  async function runDelete() {
    if (ids.length === 0) return;
    const result = await bulkDelete.mutateAsync(ids);
    setBulkNotice(
      `${kontakte(result.affected)} gelöscht.` +
        (result.skipped > 0 ? ` ${result.skipped} übersprungen.` : ''),
    );
    clearSelection();
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 className="text-2xl">Kontakte</h1>
          <p className="muted text-sm" data-numeric>
            {page ? `${page.total.toLocaleString('de-CH')} Kontakte` : 'wird geladen …'}
          </p>
        </div>
        <div className="cluster">
          <Button
            variant="ghost"
            loading={exporting}
            onClick={() => void exportieren()}
            title="Alle Kontakte, die auf die aktuellen Filter passen"
          >
            Exportieren
          </Button>
          <Link to="/kontakte/sperrliste" className="btn" data-variant="ghost">
            Sperrliste
          </Link>
          <Link to="/kontakte/dubletten" className="btn" data-variant="ghost">
            Dubletten
          </Link>
          <Link to="/kontakte/import" className="btn" data-variant="secondary">
            Importieren
          </Link>
          <Button onClick={() => setDialogOpen(true)}>Kontakt anlegen</Button>
        </div>
      </header>

      <div className="toolbar">
        <input
          className="input"
          type="search"
          placeholder="Nach Name oder E-Mail suchen …"
          value={search}
          onChange={(e) => changeFilter(() => setSearch(e.target.value))}
          style={{ maxWidth: '320px' }}
          aria-label="Kontakte durchsuchen"
        />
        <select
          className="input"
          value={status}
          onChange={(e) => changeFilter(() => setStatus(e.target.value))}
          style={{ maxWidth: '180px' }}
          aria-label="Nach Status filtern"
        >
          <option value="">Alle Status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={listId}
          onChange={(e) => changeFilter(() => setListId(e.target.value))}
          style={{ maxWidth: '220px' }}
          aria-label="Nach Liste filtern"
        >
          <option value="">Alle Listen</option>
          {lists.data?.items.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({l.contactCount})
            </option>
          ))}
        </select>
        <Button variant="ghost" onClick={() => setListManagerOpen(true)}>
          Listen verwalten
        </Button>
      </div>

      {bulkNotice && (
        <div className="alert" data-tone="success" role="status">
          {bulkNotice}
        </div>
      )}

      {/*
       * Die Aktionsleiste erscheint erst mit einer Auswahl und nennt immer die
       * Zahl. „Löschen" ohne Zahl davor ist der Knopf, den man einmal zu oft
       * drückt.
       */}
      {selected.size > 0 && (
        <div className="card">
          <div
            className="card__body"
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <strong data-numeric>{selected.size.toLocaleString('de-CH')} ausgewählt</strong>

            <select
              className="input"
              style={{ maxWidth: '220px' }}
              value={bulkListId}
              onChange={(e) => setBulkListId(e.target.value)}
              aria-label="Liste für die Massenbearbeitung"
            >
              <option value="">Liste wählen …</option>
              {lists.data?.items.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>

            <Button
              variant="secondary"
              disabled={!bulkListId}
              loading={bulkList.isPending}
              onClick={() => void runListAction('add')}
            >
              Hinzufügen
            </Button>
            <Button
              variant="secondary"
              disabled={!bulkListId}
              loading={bulkList.isPending}
              onClick={() => void runListAction('remove')}
            >
              Entfernen
            </Button>
            <Button
              variant="secondary"
              disabled={!bulkListId}
              loading={bulkList.isPending}
              onClick={() => void runListAction('move')}
              title="Aus allen bisherigen Listen nehmen und in diese eine setzen"
            >
              Verschieben
            </Button>

            <span style={{ flex: 1 }} />

            {confirmDelete ? (
              <>
                <span className="text-sm">{kontakte(selected.size)} wirklich löschen?</span>
                <Button
                  variant="danger"
                  loading={bulkDelete.isPending}
                  onClick={() => void runDelete()}
                >
                  Ja, löschen
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </Button>
            )}

            <Button variant="ghost" onClick={clearSelection}>
              Auswahl aufheben
            </Button>
          </div>

          {(bulkList.isError || bulkDelete.isError) && (
            <div className="card__body">
              <div className="alert" data-tone="danger" role="alert">
                {(bulkList.error ?? bulkDelete.error) instanceof Error
                  ? (bulkList.error ?? bulkDelete.error)!.message
                  : 'Die Aktion ist fehlgeschlagen.'}
              </div>
            </div>
          )}
        </div>
      )}

      {contacts.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {contacts.error instanceof Error
            ? contacts.error.message
            : 'Die Kontakte konnten nicht geladen werden.'}
        </div>
      )}

      {isEmpty ? (
        <EmptyState hasFilters={hasFilters} onCreate={() => setDialogOpen(true)} />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col" style={{ width: '2.5rem' }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        // Teilauswahl sichtbar machen: weder leer noch voll.
                        if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
                      }}
                      onChange={(e) => togglePage(e.target.checked)}
                      aria-label="Alle Kontakte auf dieser Seite auswählen"
                    />
                  </th>
                  <th scope="col">E-Mail</th>
                  <th scope="col">Name</th>
                  <th scope="col">Status</th>
                  <th scope="col">Land</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Score
                  </th>
                  <th scope="col">Angelegt</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {page?.items.map((contact) => (
                  <tr key={contact.id} data-selected={selected.has(contact.id)}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(contact.id)}
                        onChange={(e) => toggleOne(contact.id, e.target.checked)}
                        aria-label={`${contact.email} auswählen`}
                      />
                    </td>
                    <td>
                      <Link to="/kontakte/$id" params={{ id: contact.id }}>
                        {contact.email}
                      </Link>
                    </td>
                    <td>
                      {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td>
                      <span className="badge" data-status={contact.status}>
                        {STATUS_LABELS[contact.status] ?? contact.status}
                      </span>
                    </td>
                    <td>{contact.country ?? '—'}</td>
                    <td data-numeric style={{ textAlign: 'right' }}>
                      {contact.leadScore}
                    </td>
                    <td className="muted text-sm">
                      {new Date(contact.createdAt).toLocaleDateString('de-CH')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button
                        variant="ghost"
                        onClick={() => setSuppressContact({ id: contact.id, email: contact.email })}
                        title="Auf Sperrliste setzen"
                      >
                        🚫 Sperren
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <span className="muted text-sm">
              {page?.items.length ?? 0} von {page?.total ?? 0} angezeigt
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button
                variant="secondary"
                disabled={cursorStack.length === 0}
                onClick={() => setCursorStack((s) => s.slice(0, -1))}
              >
                Zurück
              </Button>
              <Button
                variant="secondary"
                disabled={!page?.nextCursor}
                onClick={() =>
                  setCursorStack((s) => (page?.nextCursor ? [...s, page.nextCursor] : s))
                }
              >
                Weiter
              </Button>
            </div>
          </div>
        </div>
      )}

      {dialogOpen && <NewContactDialog onClose={() => setDialogOpen(false)} />}
      {listManagerOpen && <ListManagerDialog onClose={() => setListManagerOpen(false)} />}
      {suppressContact && (
        <SuppressContactDialog
          contactId={suppressContact.id}
          email={suppressContact.email}
          onClose={() => setSuppressContact(null)}
        />
      )}
    </div>
  );
}

/**
 * Leerer Zustand mit Handlungsangebot statt "Keine Daten". Unterscheidet
 * bewusst zwischen "noch nichts da" und "Filter zu eng" — das sind zwei
 * verschiedene Probleme mit zwei verschiedenen Loesungen.
 */
function EmptyState({ hasFilters, onCreate }: { hasFilters: boolean; onCreate: () => void }) {
  if (hasFilters) {
    return (
      <div className="card">
        <div className="card__body stack" style={{ alignItems: 'flex-start' }}>
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Keine Treffer</h2>
          <p className="muted text-sm">
            Zu diesen Filtern gibt es keine Kontakte. Suchbegriff kürzen oder Filter zurücksetzen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card__body stack" style={{ alignItems: 'flex-start' }}>
        <h2 style={{ fontSize: 'var(--text-lg)' }}>Noch keine Kontakte</h2>
        <p className="muted text-sm">
          Beginne mit einem CSV-Import — Spalten werden automatisch erkannt und vor dem Übernehmen
          zur Bestätigung angezeigt.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/kontakte/import" className="btn" data-variant="primary">
            CSV importieren
          </Link>
          <Button variant="secondary" onClick={onCreate}>
            Einzelnen Kontakt anlegen
          </Button>
        </div>
      </div>
    </div>
  );
}
